import { useState, useEffect } from 'react';
import axios from 'axios';
import { Building, Plus, FileText, Download, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ManagementInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    entity_id: '', service_period: '', management_fee: '', additional_services: '',
    description: 'Property Management Services', due_date: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [invRes, propsRes] = await Promise.all([
        axios.get(`${API}/invoices?invoice_type=management`),
        axios.get(`${API}/corporations`)
      ]);
      setInvoices(invRes.data.filter(i => i.invoice_type === 'management' || i.description?.includes('Management')));
      setProperties(propsRes.data);
    } catch (error) { console.error('Failed to fetch data:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const managementFee = parseFloat(formData.management_fee) || 0;
    const additionalServices = parseFloat(formData.additional_services) || 0;
    const subtotal = managementFee + additionalServices;
    
    try {
      await axios.post(`${API}/invoices`, {
        invoice_type: 'management',
        entity_id: formData.entity_id,
        description: `${formData.description} - ${formData.service_period}`,
        subtotal: subtotal,
        tax_rate: 13,
        due_date: formData.due_date
      });
      setShowModal(false);
      setFormData({ entity_id: '', service_period: '', management_fee: '', additional_services: '', description: 'Property Management Services', due_date: '' });
      fetchData();
    } catch (error) { console.error('Failed to create invoice:', error); }
  };

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `management_invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error('Failed to download invoice:', error); }
  };

  const markAsPaid = async (invoiceId) => {
    try {
      await axios.patch(`${API}/invoices/${invoiceId}`, { status: 'paid' });
      fetchData();
    } catch (error) { console.error('Failed to update invoice:', error); }
  };

  const filteredInvoices = invoices.filter(inv => !filterStatus || inv.status === filterStatus);
  const totalOutstanding = invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + (i.total_amount || 0), 0);
  const formatCurrency = (amount) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }).format(amount || 0);

  if (loading) { return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>; }

  return (
    <div className="space-y-6" data-testid="management-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Management Fee Invoices</h1>
          <p className="text-slate-500">Invoice property owners for management services</p>
        </div>
        <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg" data-testid="new-mgmt-invoice-btn"><Plus className="w-4 h-4" /> New Invoice</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl p-5 text-white">
          <Building className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{formatCurrency(totalOutstanding)}</p>
          <p className="text-indigo-100">Outstanding Fees</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <FileText className="w-6 h-6 text-amber-500 mb-2" />
          <p className="text-2xl font-bold text-slate-900">{invoices.filter(i => i.status === 'pending').length}</p>
          <p className="text-slate-500">Pending Invoices</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <Building className="w-6 h-6 text-emerald-500 mb-2" />
          <p className="text-2xl font-bold text-emerald-600">{invoices.filter(i => i.status === 'paid').length}</p>
          <p className="text-slate-500">Paid Invoices</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Property Management Invoices:</strong> These invoices are sent to property/corporation owners for property management services including: 
          property management fees, administrative services, financial reporting, and any additional services provided.
        </p>
      </div>

      <div className="flex gap-4">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Invoice #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Property/Corporation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Due Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredInvoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{inv.entity_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{inv.description}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{inv.invoice_date}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{inv.due_date}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatCurrency(inv.total_amount)}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : inv.status === 'overdue' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{inv.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => downloadInvoice(inv.id, inv.invoice_number)} className="p-1 text-indigo-600 hover:bg-indigo-50 rounded" title="Download PDF"><Download className="w-4 h-4" /></button>
                      {inv.status !== 'paid' && <button onClick={() => markAsPaid(inv.id)} className="text-xs px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700">Mark Paid</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">New Management Fee Invoice</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Property/Corporation</label><select required value={formData.entity_id} onChange={(e) => setFormData({...formData, entity_id: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg"><option value="">Select property</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name} ({p.property_type})</option>)}</select></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Service Period</label><input type="text" required value={formData.service_period} onChange={(e) => setFormData({...formData, service_period: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="e.g., January 2026" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label><input type="text" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Management Fee</label><input type="number" required step="0.01" min="0" value={formData.management_fee} onChange={(e) => setFormData({...formData, management_fee: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="0.00" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Additional Services</label><input type="number" step="0.01" min="0" value={formData.additional_services} onChange={(e) => setFormData({...formData, additional_services: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="0.00" /></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label><input type="date" required value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">Subtotal: {formatCurrency((parseFloat(formData.management_fee) || 0) + (parseFloat(formData.additional_services) || 0))}</p>
                <p className="text-sm text-slate-600">HST (13%): {formatCurrency(((parseFloat(formData.management_fee) || 0) + (parseFloat(formData.additional_services) || 0)) * 0.13)}</p>
                <p className="text-sm font-semibold text-slate-900">Total: {formatCurrency(((parseFloat(formData.management_fee) || 0) + (parseFloat(formData.additional_services) || 0)) * 1.13)}</p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Create Invoice</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
